import { supabase } from '../../../lib/supabase';

const PIPELINE_STAGES = {
  plumbing: ['Emergency Call', 'Quote Sent', 'Scheduled', 'In Progress', 'Invoiced', 'Closed'],
  hvac: ['Inquiry', 'Diagnostic Scheduled', 'Quote Approved', 'Installation/Repair', 'Follow-up', 'Closed'],
  electrical: ['Request', 'Site Visit', 'Proposal', 'Permitted & Scheduled', 'In Progress', 'Completed'],
  handyman: ['New Request', 'Quote Sent', 'Approved', 'Scheduled', 'In Progress', 'Invoiced', 'Closed'],
  cleaning: ['Inquiry', 'Walk-through', 'Quote Sent', 'Booked', 'Completed', 'Review Requested'],
  landscaping: ['Inquiry', 'Site Assessment', 'Proposal', 'Scheduled', 'In Progress', 'Invoiced'],
  pest_control: ['Call Received', 'Inspection', 'Treatment Plan', 'Service Scheduled', 'Treated', 'Follow-up'],
  roofing: ['Lead', 'Inspection', 'Estimate', 'Contract Signed', 'Materials Ordered', 'In Progress', 'Completed'],
  painting: ['Inquiry', 'Estimate Visit', 'Quote Approved', 'Scheduled', 'Priming', 'Painting', 'Final Walk', 'Invoiced'],
  pool_service: ['Inquiry', 'Assessment', 'Service Agreement', 'Regular Schedule', 'Service', 'Invoiced'],
  moving: ['Inquiry', 'In-Home Estimate', 'Quote Sent', 'Deposit Paid', 'Packing Day', 'Moving Day', 'Delivered', 'Invoiced'],
  general_contractor: ['Lead', 'Site Visit', 'Bid Submitted', 'Contract Signed', 'Permit', 'In Progress', 'Punch List', 'Closed'],
  mobile_detailing: ['New Inquiry', 'Quote Sent', 'Deposit Paid', 'Appointment Confirmed', 'En Route', 'In Progress', 'Completed', 'Review Requested'],
  home_automation: ['New Lead', 'Site Survey', 'System Design', 'Proposal Sent', 'Contract Signed', 'Equipment Ordered', 'Installation', 'Programming & Testing', 'Client Training', 'Closed'],
  other: ['New', 'Contacted', 'Proposal', 'In Progress', 'Completed'],
};

const JOB_TYPES = {
  plumbing: ['Emergency Repair', 'Leak Detection', 'Pipe Installation', 'Drain Cleaning', 'Water Heater', 'Fixture Installation', 'Sewer Line', 'Backflow Prevention'],
  hvac: ['AC Repair', 'Heating Repair', 'Installation', 'Maintenance', 'Duct Cleaning', 'Thermostat', 'Emergency Service', 'Seasonal Tune-up'],
  electrical: ['Panel Upgrade', 'Wiring', 'Outlet Installation', 'Lighting', 'EV Charger', 'Generator', 'Smart Home', 'Emergency'],
  handyman: ['Furniture Assembly', 'TV Mounting', 'Door Repair', 'Window Repair', 'Drywall Patch', 'Caulking & Sealing', 'Shelf Installation', 'Gutter Cleaning', 'Deck Repair', 'Fence Repair', 'Tile Repair', 'General Repairs', 'Painting Touch-up', 'Weatherstripping', 'Lock & Hardware'],
  cleaning: ['Deep Clean', 'Regular Maintenance', 'Move-in/Move-out', 'Post-Construction', 'Window Cleaning', 'Carpet Cleaning', 'Office Cleaning', 'Airbnb Turnover'],
  landscaping: ['Lawn Mowing', 'Landscaping Design', 'Tree Trimming', 'Irrigation', 'Mulching', 'Fertilization', 'Snow Removal', 'Hardscaping'],
  pest_control: ['General Inspection', 'Rodent Control', 'Termite Treatment', 'Bed Bug Treatment', 'Mosquito Control', 'Ant Treatment', 'Quarterly Service'],
  roofing: ['Roof Repair', 'Full Replacement', 'Inspection', 'Gutter Cleaning', 'Storm Damage', 'Flat Roof', 'Skylight Installation'],
  painting: ['Interior Painting', 'Exterior Painting', 'Cabinet Painting', 'Deck Staining', 'Wallpaper Removal', 'Commercial Painting', 'Touch-up'],
  pool_service: ['Weekly Cleaning', 'Chemical Balance', 'Filter Cleaning', 'Equipment Repair', 'Opening/Closing', 'Leak Detection', 'Resurfacing'],
  moving: ['Local Move', 'Long-Distance Move', 'Commercial Move', 'Packing Service', 'Unpacking Service', 'Storage', 'Piano/Specialty Move', 'Junk Removal'],
  general_contractor: ['Kitchen Remodel', 'Bathroom Remodel', 'Room Addition', 'Basement Finishing', 'Deck Building', 'Roofing', 'Siding', 'Flooring'],
  mobile_detailing: ['Basic Wash & Vacuum', 'Full Interior Detail', 'Full Exterior Detail', 'Full Detail (Interior + Exterior)', 'Paint Correction', 'Ceramic Coating', 'Paint Protection Film (PPF)', 'Headlight Restoration', 'Engine Bay Cleaning', 'Odor Elimination', 'Leather Conditioning', 'Window Tinting', 'Fleet Detailing', 'Boat/RV Detailing', 'Showroom Prep'],
  home_automation: [
    'Smart Home Consultation',
    'Whole-Home Automation Install',
    'Lighting Control System',
    'Home Theater & AV Setup',
    'Security Camera Installation',
    'Alarm & Intrusion System',
    'Access Control & Smart Locks',
    'Video Doorbell Installation',
    'Home Network & Wi-Fi Setup',
    'Structured Wiring & Low-Voltage',
    'Business Network Installation',
    'Managed IT / Network Support',
    'Smart Thermostat & HVAC Control',
    'Voice Control Integration (Alexa/Google)',
    'Motorized Shades & Blinds',
    'Whole-Home Audio',
    'EV Charger Smart Integration',
    'System Upgrade & Expansion',
    'Remote Monitoring Setup',
    'Annual Maintenance & Support',
  ],
  other: ['Consultation', 'Service Call', 'Installation', 'Repair', 'Maintenance', 'Inspection'],
};

const CALL_SCRIPTS = {
  plumbing: `Thank you for calling [Business Name], this is [Agent Name]. I understand you're having a plumbing issue — can you describe what's happening? [Listen] Is this an emergency situation? [Address/location] What's the best way to reach you? I can have a technician out [timeframe]. Let me get you booked in.`,
  hvac: `Thank you for calling [Business Name], this is [Agent Name]. Are you calling about heating or cooling? [Listen] How long has the system been having issues? Is it completely down or just not working properly? [Address] What's a good callback number? Let me schedule a diagnostic visit for you.`,
  electrical: `Thank you for calling [Business Name], this is [Agent Name]. What electrical issue are you experiencing? [Listen] Is this a safety emergency — any sparks, burning smell, or power outage? [Address] I'll get a licensed electrician out to you. What's the best time for you?`,
  handyman: `Thank you for calling [Business Name], this is [Agent Name]. What handyman task can we help you with today? [Listen] Is this a repair, installation, or assembly job? How soon do you need it done? [Address] What's the best number to reach you? Let me get you scheduled — we typically have availability within [timeframe].`,
  cleaning: `Thank you for calling [Business Name], this is [Agent Name]. Are you looking for a one-time clean or recurring service? [Listen] How many bedrooms and bathrooms? Any pets or special requirements? [Address] Let me give you a quick quote and get you scheduled.`,
  landscaping: `Thank you for calling [Business Name], this is [Agent Name]. What lawn or landscaping service are you interested in? [Listen] How large is your property? Do you have an HOA? [Address] I'd love to schedule a free site assessment — when works best for you?`,
  pest_control: `Thank you for calling [Business Name], this is [Agent Name]. What type of pest issue are you dealing with? [Listen] How long have you noticed the problem? Have you tried any treatments? [Address] I can schedule an inspection — we'll identify the issue and provide a treatment plan.`,
  roofing: `Thank you for calling [Business Name], this is [Agent Name]. Are you calling about a repair, replacement, or inspection? [Listen] Is there any active leaking or storm damage? [Address] I'll schedule a free roof inspection — what day works for you?`,
  painting: `Thank you for calling [Business Name], this is [Agent Name]. Are you looking for interior or exterior painting? [Listen] Approximately how many rooms or square footage? Any specific colors or finishes in mind? [Address] Let me schedule a free estimate visit.`,
  pool_service: `Thank you for calling [Business Name], this is [Agent Name]. Are you looking for regular maintenance or do you have a specific issue? [Listen] What type of pool — chlorine or saltwater? [Address] I can schedule a service visit — what's your availability?`,
  moving: `Thank you for calling [Business Name], this is [Agent Name]. Are you planning a local or long-distance move? [Listen] What's your moving date and current address? How many bedrooms? Any large or specialty items like pianos or safes? [Address] I'd love to schedule a free in-home estimate — when works best for you?`,
  general_contractor: `Thank you for calling [Business Name], this is [Agent Name]. What type of project are you considering? [Listen] Do you have a timeline or budget in mind? Is this a renovation or new construction? [Address] I'd love to schedule a site visit to discuss your project in detail.`,
  mobile_detailing: `Thank you for calling [Business Name], this is [Agent Name]. Are you looking to book a detailing service? [Listen] What type of vehicle — car, SUV, truck, or van? What service are you interested in — basic wash, full detail, ceramic coating, or something else? [Address/location for mobile service] What's the best number to reach you? I can get you scheduled — we come to you! What day works best?`,
  home_automation: `Thank you for calling [Business Name], this is [Agent Name]. Are you interested in smart home, security, or networking services? [Listen] Is this for a residential or commercial property? Are you looking for a new installation, an upgrade to an existing system, or ongoing support? [Address] What's the best number to reach you? I'd love to schedule a free site survey — we'll design a custom solution for your property. When works best for you?`,
  other: `Thank you for calling [Business Name], this is [Agent Name]. How can I help you today? [Listen] Can you tell me more about what you need? [Address/contact info] Let me get you scheduled with the right person.`,
};

const CUSTOM_FIELDS = {
  plumbing: [
    { field_name: 'pipe_material', field_type: 'text' },
    { field_name: 'water_shutoff_location', field_type: 'text' },
    { field_name: 'is_emergency', field_type: 'boolean' },
    { field_name: 'permit_required', field_type: 'boolean' },
  ],
  hvac: [
    { field_name: 'unit_brand', field_type: 'text' },
    { field_name: 'unit_age', field_type: 'number' },
    { field_name: 'refrigerant_type', field_type: 'text' },
    { field_name: 'warranty_status', field_type: 'select' },
  ],
  electrical: [
    { field_name: 'panel_amperage', field_type: 'number' },
    { field_name: 'permit_number', field_type: 'text' },
    { field_name: 'is_emergency', field_type: 'boolean' },
    { field_name: 'arc_fault_required', field_type: 'boolean' },
  ],
  handyman: [
    { field_name: 'task_type', field_type: 'select' },
    { field_name: 'num_tasks', field_type: 'number' },
    { field_name: 'tools_required', field_type: 'text' },
    { field_name: 'materials_needed', field_type: 'boolean' },
    { field_name: 'property_type', field_type: 'select' },
    { field_name: 'access_instructions', field_type: 'text' },
  ],
  cleaning: [
    { field_name: 'square_footage', field_type: 'number' },
    { field_name: 'frequency', field_type: 'select' },
    { field_name: 'has_pets', field_type: 'boolean' },
    { field_name: 'access_code', field_type: 'text' },
  ],
  landscaping: [
    { field_name: 'lot_size', field_type: 'text' },
    { field_name: 'irrigation_present', field_type: 'boolean' },
    { field_name: 'hoa_restrictions', field_type: 'text' },
    { field_name: 'last_service_date', field_type: 'date' },
  ],
  pest_control: [
    { field_name: 'pest_type', field_type: 'text' },
    { field_name: 'treatment_type', field_type: 'text' },
    { field_name: 'warranty_months', field_type: 'number' },
    { field_name: 'chemical_sensitivity', field_type: 'boolean' },
  ],
  roofing: [
    { field_name: 'roof_type', field_type: 'select' },
    { field_name: 'roof_age', field_type: 'number' },
    { field_name: 'insurance_claim', field_type: 'boolean' },
    { field_name: 'permit_number', field_type: 'text' },
  ],
  painting: [
    { field_name: 'surface_type', field_type: 'text' },
    { field_name: 'paint_brand', field_type: 'text' },
    { field_name: 'square_footage', field_type: 'number' },
    { field_name: 'coats_required', field_type: 'number' },
  ],
  pool_service: [
    { field_name: 'pool_size_gallons', field_type: 'number' },
    { field_name: 'pool_type', field_type: 'select' },
    { field_name: 'equipment_brand', field_type: 'text' },
    { field_name: 'last_service_date', field_type: 'date' },
  ],
  moving: [
    { field_name: 'move_type', field_type: 'select' },
    { field_name: 'origin_address', field_type: 'text' },
    { field_name: 'destination_address', field_type: 'text' },
    { field_name: 'num_bedrooms', field_type: 'number' },
    { field_name: 'has_specialty_items', field_type: 'boolean' },
    { field_name: 'storage_required', field_type: 'boolean' },
  ],
  general_contractor: [
    { field_name: 'project_type', field_type: 'text' },
    { field_name: 'permit_number', field_type: 'text' },
    { field_name: 'architect_required', field_type: 'boolean' },
    { field_name: 'estimated_sqft', field_type: 'number' },
  ],
  mobile_detailing: [
    { field_name: 'vehicle_type', field_type: 'select' },
    { field_name: 'vehicle_make_model', field_type: 'text' },
    { field_name: 'vehicle_year', field_type: 'number' },
    { field_name: 'vehicle_color', field_type: 'text' },
    { field_name: 'service_location', field_type: 'text' },
    { field_name: 'paint_condition', field_type: 'select' },
    { field_name: 'ceramic_coating_warranty', field_type: 'boolean' },
    { field_name: 'interior_condition', field_type: 'select' },
  ],
  home_automation: [
    { field_name: 'property_type', field_type: 'select' },
    { field_name: 'property_sqft', field_type: 'number' },
    { field_name: 'num_floors', field_type: 'number' },
    { field_name: 'system_type', field_type: 'select' },
    { field_name: 'automation_platform', field_type: 'select' },
    { field_name: 'existing_system_brand', field_type: 'text' },
    { field_name: 'num_cameras', field_type: 'number' },
    { field_name: 'num_access_points', field_type: 'number' },
    { field_name: 'network_type', field_type: 'select' },
    { field_name: 'internet_provider', field_type: 'text' },
    { field_name: 'permit_required', field_type: 'boolean' },
    { field_name: 'permit_number', field_type: 'text' },
    { field_name: 'monitoring_plan', field_type: 'select' },
    { field_name: 'monthly_monitoring_fee', field_type: 'number' },
    { field_name: 'warranty_years', field_type: 'number' },
    { field_name: 'remote_access_enabled', field_type: 'boolean' },
  ],
  other: [
    { field_name: 'project_type', field_type: 'text' },
    { field_name: 'priority_level', field_type: 'select' },
  ],
};

const DASHBOARD_CONFIG = {
  plumbing: ['Emergency Jobs Today', 'Jobs Scheduled This Week', 'Avg Job Value', 'Parts on Order'],
  hvac: ['Service Calls Today', 'Installs This Month', 'Avg Job Value', 'Maintenance Contracts'],
  electrical: ['Jobs Today', 'Permits Pending', 'Avg Job Value', 'Emergency Calls'],
  handyman: ['Jobs Today', 'Jobs This Week', 'Avg Job Value', 'Repeat Clients'],
  cleaning: ['Recurring Clients', 'Hours Scheduled Today', 'New Bookings', 'Avg Job Value'],
  landscaping: ['Jobs This Week', 'Active Contracts', 'Avg Job Value', 'Seasonal Revenue'],
  pest_control: ['Treatments Today', 'Active Warranties', 'New Inspections', 'Avg Job Value'],
  roofing: ['Active Projects', 'Estimates Pending', 'Avg Job Value', 'Insurance Claims'],
  painting: ['Active Jobs', 'Estimates This Week', 'Avg Job Value', 'Sq Ft Painted'],
  pool_service: ['Service Routes Today', 'Active Contracts', 'Avg Job Value', 'Equipment Repairs'],
  moving: ['Moves Scheduled Today', 'Long-Distance Jobs', 'Avg Move Value', 'Packing Jobs This Week'],
  general_contractor: ['Active Projects', 'Bids Pending', 'Avg Project Value', 'Permits Active'],
  mobile_detailing: ['Details Scheduled Today', 'Ceramic Coatings This Month', 'Avg Job Value', 'Repeat Clients'],
  home_automation: ['Installs This Month', 'Active Monitoring Contracts', 'Avg Project Value', 'Site Surveys Scheduled'],
  other: ['Open Jobs', 'New Leads', 'Avg Job Value', 'Completed This Month'],
};

const STAGE_COLORS = [
  '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#14B8A6'
];

export async function seedBusinessData(orgId, businessType, orgName, preserveTrialData = false) {
  const bt = businessType || 'other';

  try {
    // 1. Delete existing pipeline stages for this org and insert new ones
    await supabase?.from('pipeline_stages')?.delete()?.eq('org_id', orgId);
    const stages = (PIPELINE_STAGES?.[bt] || PIPELINE_STAGES?.other)?.map((name, idx) => ({
      org_id: orgId,
      name,
      stage_order: idx,
      probability: Math.round(((idx + 1) / (PIPELINE_STAGES?.[bt]?.length || 5)) * 100),
      color: STAGE_COLORS?.[idx % STAGE_COLORS?.length],
    }));
    await supabase?.from('pipeline_stages')?.insert(stages);

    // 2. Delete existing call scripts and insert new one
    await supabase?.from('call_scripts')?.delete()?.eq('org_id', orgId);
    const btLabel = bt?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c?.toUpperCase());
    await supabase?.from('call_scripts')?.insert({
      org_id: orgId,
      name: `${btLabel} Inbound Script`,
      service_type: btLabel,
      content: CALL_SCRIPTS?.[bt] || CALL_SCRIPTS?.other,
      is_default: true,
    });

    // 3. Delete existing templates and insert new ones
    await supabase?.from('templates')?.delete()?.eq('org_id', orgId);
    const templates = [
      {
        org_id: orgId,
        name: `${btLabel} Service Agreement`,
        template_type: 'agreement',
        content: `SERVICE AGREEMENT\n\nThis agreement is between [Business Name] and [Client Name] for ${btLabel} services.\n\nScope of Work: [Description of services]\nLocation: [Service Address]\nScheduled Date: [Date]\nEstimated Cost: $[Amount]\n\nTerms: Payment due upon completion. [Business Name] is not responsible for pre-existing conditions. Client agrees to provide safe access to work area.\n\nSignature: _____________________ Date: __________`,
        is_default: true,
      },
      {
        org_id: orgId,
        name: `${btLabel} Estimate`,
        template_type: 'estimate',
        content: `ESTIMATE\n\nFrom: [Business Name]\nTo: [Client Name]\nDate: [Date]\nEstimate #: [Number]\n\nService: ${btLabel}\nDescription: [Service Description]\n\nLabor: $[Amount]\nMaterials: $[Amount]\nSubtotal: $[Amount]\nTax (10%): $[Amount]\nTOTAL: $[Amount]\n\nValid for 30 days.`,
        is_default: false,
      },
      {
        org_id: orgId,
        name: `${btLabel} Invoice`,
        template_type: 'invoice',
        content: `INVOICE\n\nFrom: [Business Name]\nTo: [Client Name]\nDate: [Date]\nInvoice #: [Number]\nDue Date: [Due Date]\n\nService: ${btLabel}\nDescription: [Service Description]\n\nLabor: $[Amount]\nMaterials: $[Amount]\nSubtotal: $[Amount]\nTax (10%): $[Amount]\nTOTAL: $[Amount]\n\nPayment due within 30 days.`,
        is_default: false,
      },
      {
        org_id: orgId,
        name: `${btLabel} Welcome Email`,
        template_type: 'email',
        content: `Subject: Welcome to [Business Name]!\n\nHi [First Name],\n\nThank you for choosing [Business Name] for your ${btLabel?.toLowerCase()} needs. We're excited to work with you!\n\nBest,\n[Business Name] Team`,
        is_default: false,
      },
      {
        org_id: orgId,
        name: `${btLabel} Follow-up Email`,
        template_type: 'email',
        content: `Subject: How did we do? — [Business Name]\n\nHi [First Name],\n\nThank you for choosing [Business Name]! We hope your recent ${btLabel?.toLowerCase()} service met your expectations.\n\nWe'd love to hear your feedback.\n\nBest,\n[Business Name] Team`,
        is_default: false,
      },
      {
        org_id: orgId,
        name: `${btLabel} Seasonal Promotion`,
        template_type: 'email',
        content: `Subject: Special Offer — [Business Name]\n\nHi [First Name],\n\nSpecial seasonal discount on ${btLabel?.toLowerCase()} services!\n\n🎉 [Discount]% OFF — Valid through [Date]\n\nBook now before spots fill up!\n\nBest,\n[Business Name] Team`,
        is_default: false,
      },
    ];
    await supabase?.from('templates')?.insert(templates);

    // 4. Delete existing custom fields for jobs and insert new ones
    await supabase?.from('custom_fields')?.delete()?.eq('org_id', orgId)?.eq('entity_type', 'job');
    const fields = (CUSTOM_FIELDS?.[bt] || CUSTOM_FIELDS?.other)?.map((f) => ({
      org_id: orgId,
      entity_type: 'job',
      field_name: f?.field_name,
      field_type: f?.field_type,
      required: false,
    }));
    if (fields?.length > 0) {
      await supabase?.from('custom_fields')?.insert(fields);
    }

    // 5. Fetch current org settings so we can merge (not overwrite) existing keys
    const { data: currentOrg } = await supabase?.from('organizations')?.select('settings')?.eq('id', orgId)?.single();
    const existingSettings = currentOrg?.settings || {};

    const serviceTypes = JOB_TYPES?.[bt] || JOB_TYPES?.other;
    const dashboardConfig = (DASHBOARD_CONFIG?.[bt] || DASHBOARD_CONFIG?.other)?.map((label) => ({ label }));

    // When called from Settings (preserveTrialData=true), only update business/CRM fields.
    // Never touch trial_start_date, trial_end_date, subscription_status, or onboarding_completed.
    const orgUpdate = preserveTrialData
      ? {
          business_type: bt,
          settings: {
            ...existingSettings,
            service_types: serviceTypes,
            dashboard_config: dashboardConfig,
          },
        }
      : {
          business_type: bt,
          onboarding_completed: true,
          settings: {
            ...existingSettings,
            service_types: serviceTypes,
            dashboard_config: dashboardConfig,
          },
        };

    const { data: updatedOrg, error: orgError } = await supabase
      ?.from('organizations')
      ?.update(orgUpdate)
      ?.eq('id', orgId)
      ?.select()
      ?.single();

    if (orgError) {
      console.error('Organization update error:', orgError);
      throw orgError;
    }

    if (!updatedOrg) {
      throw new Error('Organization update returned no data — RLS may have blocked the write.');
    }

    return { success: true, organization: updatedOrg };
  } catch (err) {
    console.error('Seeding error:', err);
    throw err;
  }
}
